import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from './benefitSection.module.scss';
import CtaButton from '../ctaButton/ctaButton';

// ImageComponent: Renders an image with specific styling based on the `type` prop
function ImageComponent({ type, image, mobileImage }) {
  const { width } = useWindowDimensions();
  const [finalImage, setFinalImage] = useState(image);

  // Update `finalImage` based on screen width (responsive handling)
  useEffect(() => {
    setFinalImage(width < 768 && mobileImage ? mobileImage : image);
  }, [width, mobileImage, image]);

  // Determine class and dimensions based on image type
  let className = '';
  let widthAttr = 'auto';
  let heightAttr = 'auto';

  if (type === 'icon') {
    className = styles.icon;
    widthAttr = 80;
    heightAttr = 80;
  } else if (type === 'illustration') {
    className = styles.illustration;
    widthAttr = 140;
    heightAttr = 140;
  } else if (type === 'fullwidth') {
    className = styles.fullwidth;
  } else if (type === 'leftCheckIcon') {
    className = styles.leftCheckIcon;
    widthAttr = 32;
    heightAttr = 32;
  } else {
    className = styles.illustration;
    widthAttr = 140;
    heightAttr = 140;
  }

  // Render image if available
  return finalImage ? (
    <img
      src={finalImage.path}
      loading="lazy"
      alt={finalImage.alt}
      className={className}
      width={widthAttr}
      height={heightAttr}
    />
  ) : null;
}

// PropTypes for ImageComponent
ImageComponent.propTypes = {
  type: PropTypes.oneOf(['icon', 'illustration', 'fullwidth', 'leftCheckIcon'])
    .isRequired,
  image: PropTypes.shape({
    path: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  mobileImage: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
  }),
};

/**
 * BenefitItem Component
 * This component displays a section with an image, headline, description, and a call-to-action button.
 *
 * Props:
 * - listProducts: Boolean to specify if the section displays a list of products.
 * - mobileImage: Object with `path` and `alt` for the mobile version of the image.
 * - image: Object with `path`, `alt`, `width`, and `height` for the desktop image.
 * - textIcon: String HTML content representing a text icon.
 * - imageSize: String to control image sizing (`cover`, `unset`, or null).
 * - headline: String HTML content for the headline.
 * - description: String HTML content for the description.
 * - learnMore: Object with `label`, `link`, `ctaStyle`, and `ctaTheme` for call-to-action.
 * - hasAtLeastOneImage: Boolean to indicate the presence of an image.
 * - manualImageSize: Boolean to control manual image sizing.
 * - textPosition - Text alignment for content ('center', 'left')
 * - type: String to define the image type where illustration is default type (`text`, `icon`, `illustration`, `noIcon`, `fullwidth`).
 */
function BenefitItem({
  listProducts,
  mobileImage,
  image,
  iconText = '',
  imageSize = null,
  headline,
  description,
  learnMore,
  hasAtLeastOneImage,
  manualImageSize,
  textPosition,
  type,
}) {
  /**
   * renderBenefitContent: Renders different content based on the `type` prop
   * @param {string} elementType - type of element (`text`, `icon`, `illustration`, `noIcon`)
   * @param {string} textIconElement - The HTML content for the text icon
   * @param {object} learnMoreElement - The object for learn more CTA link
   */
  const renderBenefitContent = (elementType, textIconElement) => {
    switch (elementType) {
      case 'text':
        return (
          textIconElement && (
            <div className={styles.benefitItemText}>
              <div className={styles.text}>{parse(textIconElement)}</div>
            </div>
          )
        );
      case 'icon':
      case 'illustration': {
        const imageClass = type === 'icon' ? styles.icon : styles.illustration;
        return (
          <div className={`${styles.benefitItemImg} ${imageClass}`}>
            <ImageComponent
              type={type}
              image={image}
              mobileImage={mobileImage}
            />
          </div>
        );
      }
      case 'leftCheckIcon': {
        return (
          <div className={`${styles.benefitItemImg} ${styles.leftCheckIcon}`}>
            <ImageComponent
              type={type}
              image={image}
              mobileImage={mobileImage}
            />
          </div>
        );
      }
      case 'fullwidth': {
        return (
          <div className={`${styles.benefitItemImgFullWidth}`}>
            <ImageComponent
              type={type}
              image={image}
              mobileImage={mobileImage}
            />
          </div>
        );
      }
      case 'noIcon':
        return null;
      default: {
        return (
          <div className={`${styles.benefitItemImg} ${styles.illustration}`}>
            <ImageComponent
              type={type}
              image={image}
              mobileImage={mobileImage}
            />
          </div>
        );
      }
    }
  };
  return (
    <div
      className={`${styles.benefitItem} 
        ${
          textPosition === 'left'
            ? styles['benefitItem-left']
            : styles['benefitItem-center']
        }
        ${listProducts ? styles.benefitItemProducts : ''} ${
          imageSize === 'cover' ? styles.imageCover : ''
        } ${!manualImageSize ? styles.finalImage : ''}`}
    >
      {/* Render content based on type */}
      {(type === 'icon' ||
        hasAtLeastOneImage ||
        type === 'text' ||
        type === 'illustration' ||
        type === 'leftCheckIcon' ||
        type === 'fullwidth') &&
        renderBenefitContent(type, iconText)}
      <div
        className={`${
          type === 'icon'
            ? `${styles.benefitIconContent} ${
                textPosition === 'left'
                  ? styles['benefitIconContent-left']
                  : styles['benefitIconContent-center']
              }`
            : `${styles.benefitTitleContent} ${
                textPosition === 'left'
                  ? styles['benefitTitleContent-left']
                  : styles['benefitTitleContent-center']
              }`
        }`}
      >
        {/* Render headline */}
        {headline && (
          <p className={styles.benefitItemTitle}>{parse(headline)}</p>
        )}
        {/* Render description and CTA */}
        {description && (
          <div
            className={`${styles.benefitItemContent} ${
              listProducts ? styles.benefitItemContentProducts : ''
            }`}
          >
            {parse(description)}
          </div>
        )}

        {/* Call-to-action rendering based on `ctaStyle` */}
        {learnMore && (
          <div className={styles.ctaContainer}>
            <CtaButton
              label={learnMore?.label}
              href={learnMore?.link}
              variant={learnMore?.variant || 'link'}
              color={learnMore?.color}
              className={styles.cta}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// PropTypes for type-checking
BenefitItem.propTypes = {
  listProducts: PropTypes.bool,
  mobileImage: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
  }),
  image: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  iconText: PropTypes.string,
  imageSize: PropTypes.oneOf([null, 'cover', 'unset']),
  headline: PropTypes.string,
  description: PropTypes.string,
  learnMore: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    ctaStyle: PropTypes.string,
    ctaTheme: PropTypes.string,
  }),
  hasAtLeastOneImage: PropTypes.bool,
  manualImageSize: PropTypes.bool,
  textPosition: PropTypes.oneOf(['center', 'left']),
  type: PropTypes.oneOf([
    'text',
    'icon',
    'noIcon',
    'illustration',
    'leftCheckIcon',
  ]),
};

export default BenefitItem;
